import { toJs } from 'xml-js-reader';
import { ErrorMessage } from '../../common/components/utils/error';

export interface Stage {
    id: string;
    name: string;
    isStarting: boolean;
    incoming: [string?];
    incomingError: [string?];
    outgoing: [string?];
    outgoingError: [string?];
};

interface FilterProps {
    (callback: Function): JS[];
};

interface FindByTagProps {
    (tag: string): JS[];
};

interface FindByParamProps {
    (key: string, value: string): JS[];
};

interface FindByPathProps {
    (paths: string[]): JS[];
};

export interface JS {
    params: any;
    tag: string;
    xmlTag: string;
    children: JS[];
    text?: string;
    filter: FilterProps;
    findByTag: FindByTagProps;
    findByParam: FindByParamProps;
    findByPath: FindByPathProps;
};

const createStage = (id: string, name: string): Stage => {
    return {
        id,
        name,
        isStarting: false,
        incoming: [],
        outgoing: [],
        incomingError: [],
        outgoingError: []
    }
}

const readStage = (js: JS, element: JS | string, keepBoundary = false): Stage => {
    let jsStage;
    if (typeof element === "string") {
        jsStage = js.findByParam("id", element)[0];
    } else {
        jsStage = element;
    }
    const stage = createStage(jsStage.params.id, jsStage.params.name);
    for (const child of jsStage.children) {
        if (child.tag !== "bpmn:incoming" && child.tag !== "bpmn:outgoing") {
            continue;
        }
        const type = child.tag.substring(5);
        const flowId = child.children[0].text;
        const flow = js.findByParam("id", flowId)[0];
        const isError = flow.children.length > 0 && flow.children[0].tag === "bpmn:conditionExpression";
        const value = type === "incoming" ? flow.params.sourceRef : flow.params.targetRef;
        if (keepBoundary || (value !== "EndEvent" && value !== "StartEvent")) {
            stage[`${type}${isError ? "Error" : ""}`].push(value);
        }
        if (value === "StartEvent" && type === "incoming") {
            stage.isStarting = true;
        }
    }
    return stage;
};

const readStageChildrenByType = (js: JS, stage: Stage, type: string, stagesRead: Object, stages: [Stage?]) => {
    for (const child of stage[type]) {
        if (stagesRead[child] !== true) {
            stagesRead[child] = true;
            const stageChild = readStage(js, child);
            // Before to really add we need add the incoming child
            readStageChildrenIn(js, stageChild, stagesRead, stages);
            stages.push(stageChild);
            // After we add the kid we look for his children
            readStageChildrenOut(js, stageChild, stagesRead, stages);
        }
    }
};

const readStageChildrenIn = (js: JS, stage: Stage, stagesRead: Object = { "EndEvent": true }, stages: [Stage?] = []) => {
    readStageChildrenByType(js, stage, "incoming", stagesRead, stages);
    return stages;
};

const readStageChildrenOut = (js: JS, stage: Stage, stagesRead: Object = { "EndEvent": true }, stages: [Stage?] = []) => {
    readStageChildrenByType(js, stage, "outgoing", stagesRead, stages);
    readStageChildrenByType(js, stage, "outgoingError", stagesRead, stages);
    return stages;
};

export const readStages = (js: JS): [Stage?] => {
    return readStageChildrenOut(js, readStage(js, "StartEvent"));
};

const PREFIX = "resources.workflow.errors.";

const addError = (errors: ErrorMessage[], code: string, args?: Stage) => {
    const key = `${PREFIX}${code}`;
    args.name = args.name || "";
    errors.push({ key, args });
    return errors;
};

const getAll = (js: JS, stage: Stage, type = "incoming", stagesRead = {}, keepBoundary = true): Object => {
    for (const child of stage[type]) {
        if (stagesRead[child] === undefined) {
            const stageChild = readStage(js, child, keepBoundary);
            stagesRead[child] = stageChild;
            getAll(js, stageChild, type, stagesRead, keepBoundary);
        }
    }
    return stagesRead;
};



const checkTask = (js: JS, jsTask: JS, errors = []): ErrorMessage[] => {
    const stage = readStage(js, jsTask, true);
    if (stage.incoming.length === 0 && stage.incomingError.length === 0) {
        // Si aucune entrée normal et en erreur
        addError(errors, "stageWithoutIn", stage)
    }
    if (stage.outgoing.length === 0) {
        // Si aucune sortie normal
        addError(errors, "stageNeedOutgoing", stage)
    }
    const stageIncoming = getAll(js, stage);
    const stagesKey = Object.keys(stageIncoming);
    let hasStartEvent = false;
    for (const key of stagesKey) {
        if (stage.id === key) {
            addError(errors, "stageReferenceItselfIncoming", stage);
        } else if ("StartEvent" === key) {
            hasStartEvent = true;
        }
    }
    // We don't see startEvent in incoming so we need look all his incoming error & in
    if (!hasStartEvent) {
        const newStageIncomingFromError = getAll(js, stage, "incomingError");
        const stages = Object.values(stageIncoming);
        for (const child of stages) {
            getAll(js, child, "incomingError", newStageIncomingFromError);
        }
        const newStage = Object.values(newStageIncomingFromError);
        for (const child of newStage) {
            getAll(js, child, "incoming", newStageIncomingFromError);
            getAll(js, child, "incomingError", newStageIncomingFromError);
        }
        const newStageKey = Object.keys(newStageIncomingFromError);
        for (const key of newStageKey) {
            if ("StartEvent" === key) {
                hasStartEvent = true;
            }
        }
    }
    if (!hasStartEvent) {
        addError(errors, "stageShouldBeConnectToStart", stage);
    }
    return errors;
};

/**
 *  L'entrée doit être connecté à la sortie dans un chemin sans erreur
 */
const checkEndConnectToStart = (js: JS): boolean => {
    // we don't keep boundary here to avoid a direct flux start to end
    const endStage = readStage(js, "EndEvent");
    const stages = getAll(js, endStage);
    const stagesKey = Object.keys(stages);
    for (const key of stagesKey) {
        if ("StartEvent" === key) {
            return true;
        }
    }
    return false;
}

export const checkWorkflow = (js: JS, errors = []): ErrorMessage[] => {
    if (!checkEndConnectToStart(js)) {
        addError(errors, "endShouldBeConnectedToStart");
    }
    const tasks = js.findByTag("bpmn:task");
    if (tasks.length > 0) {
        // On vérifie chaque étape
        for (const task of tasks) {
            checkTask(js, task, errors);
        }
    } else {
        // Si aucune tasks c'est une erreur
        addError(errors, "atLeastOne")
    }
    return errors;
};

export const readModel = (xml: string): JS => {
    return toJs(xml);
}